"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import Button from "@mui/material/Button";
import { Box } from "@mui/material";

import {
  type AppLocale,
  isValidLocale,
  getDefaultLocaleByDomain,
} from "@/i18n/config";

const languages: { code: AppLocale; name: string; flag: string }[] = [
  { code: "en", name: "EN", flag: "/assets/flags/en.svg" },
  { code: "zh", name: "ZH", flag: "/assets/flags/ch.svg" },
];

const LanguageSwitcher: React.FC = () => {
  const router = useRouter();
  const pathname = usePathname();

  // ✅ default locale ตาม domain (client-side)
  const [domainDefault, setDomainDefault] = useState<AppLocale>("en");

  useEffect(() => {
    const host = typeof window !== "undefined" ? window.location.host : "";
    setDomainDefault(getDefaultLocaleByDomain(host));
  }, []);

  const [currentLanguage, setCurrentLanguage] = useState<AppLocale>("en");

  useEffect(() => {
    if (!pathname) return;

    const raw = pathname.replace(/^\/+/, "");
    const segments = raw === "" ? [] : raw.split("/");
    const first = segments[0];

    if (first && isValidLocale(first)) {
      setCurrentLanguage(first);
    } else {
      // ✅ ไม่มี prefix → ถือว่าเป็น default locale ของ domain
      setCurrentLanguage(domainDefault);
    }
  }, [pathname, domainDefault]);

  const selectedLanguage = useMemo(
    () => languages.find((lang) => lang.code === currentLanguage) || languages[0],
    [currentLanguage]
  );

  const buildPathForLocale = (targetLocale: AppLocale): string => {
    const raw = (pathname ?? "/").replace(/^\/+/, "");
    const segments = raw === "" ? [] : raw.split("/");

    const first = segments[0];
    const hasLocalePrefix = !!(first && isValidLocale(first));

    const contentSegments = hasLocalePrefix ? segments.slice(1) : segments;

    // ✅ ซ่อน default locale: default → ไม่มี prefix
    if (targetLocale === domainDefault) {
      return contentSegments.length === 0 ? "/" : `/${contentSegments.join("/")}`;
    }

    // non-default → มี prefix
    return contentSegments.length === 0
      ? `/${targetLocale}`
      : `/${targetLocale}/${contentSegments.join("/")}`;
  };

  const handleLanguageSelect = (langCode: AppLocale) => {
    router.push(buildPathForLocale(langCode), { scroll: false });
  };

  return (
    <Box className="language-switcher">
      {languages.map((language) => (
        <Box key={language.code}>
          {selectedLanguage.code === language.code ? null : (
            <Button
              onClick={() => handleLanguageSelect(language.code)}
              className="language-switcher-menu"
            >
              {language.name}
            </Button>
          )}
        </Box>
      ))}
      <Button className="language-switcher-button" sx={{ maxWidth: 34 }}>
        {selectedLanguage.name}
      </Button>
    </Box>
  );
};

export default LanguageSwitcher;
